import type { AssessmentAttempt, AssessmentType } from '../apiClient';

/**
 * The free-attempt product rule, in one place.
 *
 * Three completed attempts per assessment type. The database enforces the same
 * number independently — `0001_init_schema.sql` describes `assessment_attempts`
 * as "shaped for the 3-free-attempt-per-type product rule" and `0003` carries a
 * `CHECK (1..3)` — so this constant is the browser's copy of a rule the server
 * owns, not the rule itself. It exists so the interface can say "one attempt
 * left" before the visitor answers fifty questions and is refused.
 *
 * It lived as a module-private literal inside `AssessmentRunner` until the
 * dashboard needed the same number. Two copies of a limit is how a dashboard
 * comes to promise an attempt the runner then refuses, so there is one.
 */
export const FREE_LIMIT = 3;

/** Completed attempts of one type. `status` is the server's word, not a guess. */
export function completedCount(
  attempts: readonly AssessmentAttempt[],
  type: AssessmentType,
): number {
  return attempts.filter((a) => a.assessment_type === type && a.status === 'completed').length;
}

/**
 * Free attempts left for one type, floored at zero.
 *
 * Floored rather than allowed to go negative: the server is the authority on
 * eligibility, and if it ever admits a fourth completed attempt the interface
 * should say "none left" rather than "-1 left".
 */
export function remainingAttempts(
  attempts: readonly AssessmentAttempt[],
  type: AssessmentType,
): number {
  return Math.max(0, FREE_LIMIT - completedCount(attempts, type));
}
